<template>
    <div class="page-layout">
        <div :class="{'page-overlay': true, active: overlayMessage != null}" aria-hidden="true">
            <div class="page-overlay__content">
                <LoadingIcon />
                <p class="headline-l text-rothcoWhite">{{ overlayMessage }}</p>
            </div>
        </div>
        <div ref="header" :class="{
            'sticky-wrapper': arrivedAtTop || !!animation || !breakpoints.smaller('laptop'),
            ...(animation ? {[animation]: true} : null)
            }
        ">
            <PromoBar
                v-if="!isApiServer"
                id="promoBar"
                :is_dealer="user.is_dealer"
                ref="promoBar"
            />
            <GlobalHeader
                :categoryHeaders="categories"
                :user="user"
                :list="currentList"
            />
        </div>
        <div v-if="notificationMessage" :class="{
            'global': true,
            'notification': true,
            'notification-error': notificationMessage.type === 'error',
            'notification-success': notificationMessage.type === 'success',
            'notification-info': notificationMessage.type === 'info',
            'notification-warning': notificationMessage.type === 'warning',
            'bg-rothcoBlack': notificationMessage.type === 'default'
            }">
            <p class="paragraph-default"> 
                {{ notificationMessage.message }}
            </p>
        </div>
        <div ref="headerBottom" style="height:0;"></div>
        <DynamicDialog />
        <ResellerAgreementModal />
        <main class="page-layout__main">
            <slot ref="slottedContent"/>
        </main>
        <CompareSticky v-if="!isApiServer" />
        <GlobalFooter />
    </div>
</template>

<script setup lang="ts">
    import GlobalHeader from "@/components/GlobalHeader.vue";
    import GlobalFooter from "@/components/GlobalFooter.vue";
    import PromoBar from "@/components/PromoBar.vue";
    import LoadingIcon from "@icons/loading.svg";
    import {
        ref,
        watch,
        computed,
        type ComputedRef,
        onServerPrefetch,
        onMounted,
        onUnmounted,
        inject,
        toRefs
    } from "vue";
    import { usePage } from "@inertiajs/vue3";
    import { type User } from "@interfaces/User";
    import { v4 as uuidv4 } from "uuid";
    import { router } from "@inertiajs/vue3";
    import NProgress from "nprogress";
    // import "../../ziggy.d.ts";
    import { useFavoritesStore } from "@/store/favorites";
    import { useScroll, useElementVisibility } from "@vueuse/core";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";
    import CompareSticky from "@/components/CompareSticky.vue";
    import ResellerAgreementModal from "@/components/account/ResellerAgreementModal.vue";
    import { usePageViewTracking } from "@/helpers/composables/usePageViewTracking";
    import { setSecureCookie } from "@/helpers/cookies";
    import { useQuantcast } from "@/helpers/composables/quantcast";
    let { pageViewEvent } = useQuantcast();
    // import { useRecaptchaProvider } from "vue-recaptcha";
    // useRecaptchaProvider();
    let promoBar = ref();
    const route:Function|undefined = inject("route");
    const settingStore = useSettingsStore();
    let { setFreeShippingAmount, setNotificationMessage } = settingStore;
    let { overlayMessage, notificationMessage, categories: localStorageCategories, freeShippingAmount,  isApiServer } = storeToRefs(settingStore);
    let props = defineProps({
        categories: {
            type: Array,
            default: () => []
        }
    });
    let user = computed(()=>{
        if(usePage().props.user?.user_id){
            return usePage().props.user;
        }
        return {
            user_id: null,
            username: null,
            email: null,
            is_dealer: false,
            dealer_no: null
        };
    })
    if(Array.isArray(usePage().props.categories)){
        localStorageCategories.value = usePage().props.categories;
    }

    let categories = computed(() => {
        let cats =  usePage().props.categories ?? localStorageCategories.value; ;

        return cats?.filter((cat) => cat.is_active == 1 && cat.seo_name)
            ?.map?.((cat) => {
                return {
                    shopAllLink: route("category.shop-all", [cat.seo_name]),
                    url: `category.show`,
                    urlParams: [cat.seo_name],
                    text: cat.category_name,
                    position: "header",
                    sales: false,
                    sort: null,
                    palleteLinks: {
                        military: {
                            href: route("category.shop-all", {
                                seo_name: cat.seo_name,
                                'ss[groups]': 'Military'
                            }),
                        },
                        tactical: {
                            href: route("category.shop-all", {
                                seo_name: cat.seo_name,
                                'ss[groups]': 'Tactical'
                            }),
                        },
                        work: {
                            href: route("category.shop-all", {
                                seo_name: cat.seo_name,
                                'ss[groups]': 'Work'
                            }),
                        },
                        outdoor: {
                            href: route("category.shop-all", {
                                seo_name: cat.seo_name,
                                'ss[groups]': 'Outdoor'
                            }),
                        },
                    },
                    secondary:
                        cat.child_category.filter(child => child.seo_name)?.map((child) => {
                            return {
                                shopAllLink: route("category.shop-all", [
                                    child.seo_name
                                ]),
                                url: `category.show`,
                                urlParams: [child.seo_name],
                                text: child.category_name,
                                position: "header",
                                sales: false,
                                sort: null,
                                tertiary: child.child_category?.map(
                                    (grandchild) => {
                                        return {
                                            url: "category.show",
                                            urlParams: [grandchild.seo_name],
                                            text: grandchild.category_name,
                                            position: "header",
                                            sales: false,
                                            sort: null
                                        };
                                    }
                                )
                            };
                        }) ?? []
                };
            });
    });


    let currentList = computed(() => {
        return usePage().props.current_list;
    });
    let { getFavorites } = useFavoritesStore();


    let breakpoints = {
        current: () => [],
        smaller: (str) => false,
        larger: (str) => false
    };
    const arrivedAtTop = ref(false);
    const animation = ref(null);

    let slottedContent = ref();
    let wasScrollingUp = ref(false);
    let wasScrollingDown = ref(false);
    let timeoutRef = ref();
    let scrollUpTimeoutRef = ref();
    let headerBottom = ref(null); //template ref
    let removeBeforeEvent = ()=>{};
    let removeStartEvent = ()=>{};
    let removeFinishEvent = ()=>{};
    let removePopstateListener = () => {};
    let removeFetchErrorListener = () => {};

    let removeNavigateEvent = ()=>{};

    const applyGlobalMessageFlash = () => {
        if (usePage().props.flash?.globalMessage) {
            setNotificationMessage(usePage().props.flash.globalMessage);
        }
    };

    onMounted(() => {
            //set cookies for _isuid, ssUserId and ssSessionIdNamespace. These are used for tracking user activity. isuid and ssUserId should be the same and last for 1 year. ssSessionIdNamespace should be unique for each session.
        let isuid = uuidv4();
        let ssUserId = isuid;
        let ssSessionIdNamespace = uuidv4();
        if(user.value.user_id != null) {
            setSecureCookie('ssShopperId', user.value.username, { expires: 365 });
        }

        setSecureCookie('_isuid', isuid, { expires: 365 });
        setSecureCookie('ssUserId', ssUserId, { expires: 365 });
        setSecureCookie('ssSessionIdNamespace', ssSessionIdNamespace, { expires: 365 });

        usePageViewTracking();
        const onPopState = () => {
            if(localStorageCategories.value == null || localStorageCategories.value?.length == 0){
                router.reload({
                    only: ['categories']
                });
            }
        };
        window.addEventListener('popstate', onPopState);
        removePopstateListener = () => window.removeEventListener('popstate', onPopState);

        if(![null,undefined].includes(usePage().props.free_shipping_amount)){
            setFreeShippingAmount(usePage().props.free_shipping_amount);
        }

        applyGlobalMessageFlash();

        removeBeforeEvent =  router.on('before', (event) => {
            if(localStorageCategories.value?.length == 0 || localStorageCategories.value == null){
                event.detail.visit.headers['X-Include-Categories'] = 'true'
            }
            
            if([null,undefined].includes(freeShippingAmount.value)){
                event.detail.visit.headers['X-Include-FreeShippingAmount'] = true;
            }
        });
        removeStartEvent = router.on("start", () => {
            if (NProgress.isStarted() == false) {
                NProgress.start();
            }
        });
        removeNavigateEvent = router.on("navigate", (event) => {
            pageViewEvent(user.value.email);
        });
        removeFinishEvent = router.on("finish", () => {
            if(usePage().props.categories){
                localStorageCategories.value = usePage().props.categories;
            }

            if(![null,undefined].includes(usePage().props.free_shipping_amount)){
                setFreeShippingAmount(usePage().props.free_shipping_amount);
            }

            applyGlobalMessageFlash();
            NProgress.done();
        });

       

        const onFetchError = (event: Event) => {
            let statusCode = (event as CustomEvent).detail.error.response?.status;
            let notificationMessage = {
                type: 'error',
                message: ''
            }
            switch(statusCode){
                case 401:
                    notificationMessage.message = "You are not authorized to perform this action"
                    break;
                case 403:
                    notificationMessage.message = "You are forbidden from performing this action";
                    break;
                case 404:
                    notificationMessage.message = "The requested resource was not found";
                    break;
                case 419:
                    notificationMessage.message = "Your session has expired. Please refresh the page and try again";
                    break;
                case 500:
                    notificationMessage.message = 'An error occurred while processing your request. Please try again later.';
                    break;
                default:
                    notificationMessage.message = 'An error occurred while processing your request. Please try again later.';
                    break;
            }
            setNotificationMessage(notificationMessage);
        };
        window.addEventListener('fetchError', onFetchError);
        removeFetchErrorListener = () => window.removeEventListener('fetchError', onFetchError);


        // getFavorites();


        let { directions, arrivedState, y:yPos } = useScroll(window);

        let headerIsVisible = useElementVisibility(headerBottom);

        let { top: isScrollingUp, bottom: isScrollingDown  } = toRefs(directions);

        breakpoints = useRothcoBreakpoints();

        const arrivedAtTopComputed = computed(()=>{
            // checking y scrolling position necessary for safari browsers
            return arrivedState?.top || yPos?.value < 0;
        })
        watch(arrivedAtTopComputed, (newVal)=>{
            arrivedAtTop.value = newVal;
            if(newVal){
                animation.value = null;
            }
        }, {immediate: true})

        const startedScrollingUpFrom = ref(null);
        const pixelsScrolledUp = computed(()=>{
            if(startedScrollingUpFrom.value !== null){
                return startedScrollingUpFrom.value - yPos.value;
            }else{
                return 0;
            }
        });

        watch(()=>(yPos.value),(newYPos,oldYPos)=>{
            if(newYPos < oldYPos && !arrivedAtTopComputed.value && animation.value !== 'sticky-animate-show'){
                if(startedScrollingUpFrom.value === null) startedScrollingUpFrom.value = newYPos;
            }else{
                startedScrollingUpFrom.value = null;
            }
        })
        watch([isScrollingUp, isScrollingDown, pixelsScrolledUp], 
        ([newScrollUp, newScrollDown, newPixelsScrolledUp])=>{
            if(!breakpoints.current().value.includes('laptop')){
                animation.value = 'sticky-animate-show';
                return;
            };
            if(newScrollUp || newScrollDown){
                timeoutRef.value = setTimeout(()=>{
                    if(newScrollUp){
                        if(!wasScrollingUp.value) {
                            scrollUpTimeoutRef.value = setTimeout(()=>{
                                if(isScrollingUp.value || arrivedAtTop.value){
                                    wasScrollingUp.value = true;
                                    if(!headerIsVisible.value || animation.value === 'sticky-animate-hide'){
                                        animation.value = 'sticky-animate-show';
                                    }else{
                                        animation.value = null;
                                    }
                                }
                                
                            }, 4000);
                        }
                        if(newPixelsScrolledUp > 1000){
                            clearTimeout(scrollUpTimeoutRef.value);
                            if(isScrollingUp.value || arrivedAtTop.value){
                                wasScrollingUp.value = true;
                                if(!headerIsVisible.value || animation.value === 'sticky-animate-hide'){
                                    animation.value = 'sticky-animate-show';
                                }else{
                                    animation.value = null;
                                }
                            }
                        }
                        if(wasScrollingDown.value) wasScrollingDown.value = false;

                    }else if(newScrollDown){
                        clearTimeout(scrollUpTimeoutRef.value);
                        if(!wasScrollingDown.value) wasScrollingDown.value = true;
                        if(wasScrollingUp.value) {
                            wasScrollingUp.value = false;
                        }

                        if(animation.value === 'sticky-animate-show'){
                            animation.value = 'sticky-animate-hide';
                        }
                    }
                }, 10);
            }
        })
    });


    onUnmounted(()=>{
        removeBeforeEvent();
        removeStartEvent();
        removeFinishEvent();
        removeFetchErrorListener();
        removePopstateListener();
    })
</script>
